import './PropertyFilters.scss'

import { BindLogic, useActions, useValues } from 'kea'
import React, { useState } from 'react'

import { TaxonomicPropertyFilter } from 'lib/components/PropertyFilters/components/TaxonomicPropertyFilter'
import {
    AllowedProperties,
    ExcludedOperators,
    ExcludedProperties,
    SelectingKeyOnly,
    TaxonomicFilterGroupType,
    TaxonomicFilterProps,
} from 'lib/components/TaxonomicFilter/types'
import { useOnMountEffect } from 'lib/hooks/useOnMountEffect'
import { LogicalRowDivider } from 'scenes/cohorts/CohortFilters/CohortCriteriaRowBuilder'

import { AnyDataNode, DatabaseSchemaField } from '~/queries/schema/schema-general'
import { AnyPropertyFilter, FilterLogicalOperator } from '~/types'

import { FilterRow } from './components/FilterRow'
import { OperatorValueSelectProps } from './components/OperatorValueSelect'
import { propertyFilterLogic } from './propertyFilterLogic'

export interface PropertyFiltersProps {
    endpoint?: string | null
    propertyFilters?: AnyPropertyFilter[] | null
    onChange: (filters: AnyPropertyFilter[]) => void
    pageKey: string
    showConditionBadge?: boolean
    disablePopover?: boolean
    taxonomicGroupTypes?: TaxonomicFilterGroupType[]
    taxonomicFilterOptionsFromProp?: TaxonomicFilterProps['optionsFromProp']
    metadataSource?: AnyDataNode
    showNestedArrow?: boolean
    eventNames?: string[]
    schemaColumns?: DatabaseSchemaField[]
    dataWarehouseTableName?: string
    logicalRowDivider?: boolean
    orFiltering?: boolean
    propertyGroupType?: FilterLogicalOperator | null
    addText?: string | null
    editable?: boolean
    buttonText?: string
    buttonClassName?: string
    buttonSize?: 'xsmall' | 'small' | 'medium'
    hasRowOperator?: boolean
    sendAllKeyUpdates?: boolean
    allowNew?: boolean
    openOnInsert?: boolean
    errorMessages?: JSX.Element[] | null
    propertyAllowList?: AllowedProperties
    excludedProperties?: ExcludedProperties
    allowRelativeDateOptions?: boolean
    disabledReason?: string
    excludedOperators?: ExcludedOperators
    selectingKeyOnly?: SelectingKeyOnly
    hideBehavioralCohorts?: boolean
    addFilterDocLink?: string
    operatorAllowlist?: OperatorValueSelectProps['operatorAllowlist']
    hogQLGlobals?: Record<string, any>
    /**
     * `'input'` renders the replay-style input-box add-filter trigger; `'button'`
     * (the default) renders a button. Only has an effect on the rebuild menu
     * (`TAXONOMIC_FILTER_MENU_REBUILD`).
     */
    triggerVariant?: 'button' | 'input'
}

export function PropertyFilters({
    propertyFilters = null,
    onChange,
    pageKey,
    showConditionBadge = false,
    disablePopover = false, // use bare PropertyFilter without popover
    taxonomicGroupTypes,
    taxonomicFilterOptionsFromProp,
    metadataSource,
    showNestedArrow = false,
    eventNames = [],
    schemaColumns = [],
    dataWarehouseTableName,
    orFiltering = false,
    logicalRowDivider = false,
    propertyGroupType = null,
    addText = null,
    buttonText = 'Filter',
    buttonClassName = '',
    editable = true,
    buttonSize,
    hasRowOperator = true,
    sendAllKeyUpdates = false,
    allowNew = true,
    openOnInsert = false,
    errorMessages = null,
    propertyAllowList,
    excludedProperties,
    allowRelativeDateOptions,
    disabledReason = undefined,
    excludedOperators,
    selectingKeyOnly,
    hideBehavioralCohorts,
    addFilterDocLink,
    operatorAllowlist,
    hogQLGlobals,
    triggerVariant = 'button',
}: PropertyFiltersProps): JSX.Element {
    const logicProps = { propertyFilters, onChange, pageKey, sendAllKeyUpdates }
    const { filters, filtersWithNew, filterIds, filterIdsWithNew } = useValues(propertyFilterLogic(logicProps))
    const { remove, setFilter } = useActions(propertyFilterLogic(logicProps))
    const [allowOpenOnInsert, setAllowOpenOnInsert] = useState<boolean>(false)

    const displayedFilters = allowNew && editable ? filtersWithNew : filters
    const displayedFilterIds = allowNew && editable ? filterIdsWithNew : filterIds

    // do not open on initial render, only open if newly inserted
    useOnMountEffect(() => setAllowOpenOnInsert(true))

    return (
        <div className="PropertyFilters">
            {showNestedArrow && !disablePopover && (
                <div className="PropertyFilters__prefix">
                    <>&#8627;</>
                </div>
            )}
            <div className="PropertyFilters__content max-w-full">
                <BindLogic logic={propertyFilterLogic} props={logicProps}>
                    {displayedFilters.map((item: AnyPropertyFilter, index: number) => {
                        return (
                            <React.Fragment key={displayedFilterIds[index]}>
                                {logicalRowDivider && index > 0 && index !== displayedFilters.length - 1 && (
                                    <LogicalRowDivider logicalOperator={FilterLogicalOperator.And} />
                                )}
                                <FilterRow
                                    item={item}
                                    index={index}
                                    totalCount={displayedFilters.length - 1} // empty state
                                    filters={displayedFilters}
                                    pageKey={pageKey}
                                    showConditionBadge={showConditionBadge}
                                    disablePopover={disablePopover || orFiltering}
                                    label={buttonText}
                                    labelClassName={buttonClassName}
                                    size={buttonSize}
                                    onRemove={remove}
                                    orFiltering={orFiltering}
                                    editable={editable}
                                    filterComponent={(onComplete) => (
                                        <TaxonomicPropertyFilter
                                            pageKey={pageKey}
                                            index={index}
                                            filters={filters}
                                            setFilter={setFilter}
                                            onComplete={onComplete}
                                            orFiltering={orFiltering}
                                            taxonomicGroupTypes={taxonomicGroupTypes}
                                            metadataSource={metadataSource}
                                            eventNames={eventNames}
                                            schemaColumns={schemaColumns}
                                            dataWarehouseTableName={dataWarehouseTableName}
                                            propertyGroupType={propertyGroupType}
                                            disablePopover={disablePopover || orFiltering}
                                            addText={addText}
                                            hasRowOperator={hasRowOperator}
                                            propertyAllowList={propertyAllowList}
                                            excludedProperties={excludedProperties}
                                            taxonomicFilterOptionsFromProp={taxonomicFilterOptionsFromProp}
                                            allowRelativeDateOptions={allowRelativeDateOptions}
                                            excludedOperators={excludedOperators}
                                            selectingKeyOnly={selectingKeyOnly}
                                            hideBehavioralCohorts={hideBehavioralCohorts}
                                            size={buttonSize}
                                            addFilterDocLink={addFilterDocLink}
                                            editable={editable}
                                            operatorAllowlist={operatorAllowlist}
                                            hogQLGlobals={hogQLGlobals}
                                            triggerVariant={triggerVariant}
                                        />
                                    )}
                                    errorMessage={errorMessages && errorMessages[index]}
                                    openOnInsert={allowOpenOnInsert && openOnInsert}
                                    disabledReason={disabledReason}
                                />
                            </React.Fragment>
                        )
                    })}
                </BindLogic>
            </div>
        </div>
    )
}
